import { useCallback } from 'react';
import { useTranslate } from 'react-admin';

import { useSPPScannerContext } from './SPPScannerProvider';

export enum ScannerConnectStatus {
  authorized = 'authorized',
  /** The operator dismissed the browser device picker. */
  cancelled = 'cancelled',
  failed = 'failed',
}

export type ScannerConnectResult =
  | { status: ScannerConnectStatus.authorized }
  | { status: ScannerConnectStatus.cancelled }
  | { status: ScannerConnectStatus.failed; message: string };

export interface UseScannerConnectResult {
  isConnected: boolean;
  isDeviceAuthorized: boolean;
  isReconnecting: boolean;
  connect: () => Promise<ScannerConnectResult>;
  forget: () => Promise<void>;
}

/**
 * Connection side of the global scanner: the state flags plus a connect attempt that
 * never throws. How the outcome is shown stays with the caller — a toast from the app
 * bar control, an inline message in the inventorization panel.
 */
export const useScannerConnect = (): UseScannerConnectResult => {
  const translate = useTranslate();
  const {
    isConnected,
    isDeviceAuthorized,
    isReconnecting,
    requestDeviceAuthorization,
    forgetDeviceAuthorization,
  } = useSPPScannerContext();

  const connect = useCallback(async (): Promise<ScannerConnectResult> => {
    try {
      const authorized = await requestDeviceAuthorization();
      return authorized
        ? { status: ScannerConnectStatus.authorized }
        : { status: ScannerConnectStatus.cancelled };
    } catch (err) {
      console.error('Failed to authorize the scanner:', err);
      return {
        status: ScannerConnectStatus.failed,
        message: translate('scanner.notifications.connection_failed'),
      };
    }
  }, [requestDeviceAuthorization, translate]);

  const forget = useCallback(async () => {
    await forgetDeviceAuthorization();
  }, [forgetDeviceAuthorization]);

  return { isConnected, isDeviceAuthorized, isReconnecting, connect, forget };
};
